import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Paper, Typography } from '@material-ui/core';
import IntlMessages from 'util/IntlMessages';
import PlaceholderSpinner from 'components/kea/placeholders/PlaceholderSpinner';
import PlaceholderError from '../placeholders/PlaceholderError';

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: 'hidden',
    borderRadius: 8,
    border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.12)' : '#ddeae5'}`,
    background: theme.palette.type === 'dark' ? undefined : '#fff',
    boxShadow: theme.palette.type === 'dark' ? 'none' : '0 1px 3px rgba(30, 34, 72, 0.06)',
  },
  header: {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : '#eaf3f0'}`,
  },
  body: {
    padding: '20px 24px',
  },
  tableRoot: {
    borderRadius: 10,
    borderColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.12)' : '#dfe8e4',
    boxShadow: theme.palette.type === 'dark' ? 'none' : '0 1px 2px rgba(30, 34, 72, 0.025)',
  },
  tableHeader: {
    minHeight: 64,
    alignItems: 'center !important',
    padding: '12px 16px',
    background: theme.palette.type === 'dark' ? undefined : '#fbfdfc',
    '& h3': {
      color: theme.palette.type === 'dark' ? undefined : '#1e2248',
      fontSize: 16,
      fontWeight: 650,
      lineHeight: 1.35,
    },
    '& p': {
      color: theme.palette.type === 'dark' ? undefined : '#687184',
      fontSize: 12,
      lineHeight: 1.45,
    },
  },
  tableBody: {
    padding: 0,
  },
}));

const CardContainer = ({
  error = null,
  children,
  isFetching = false,
  headerId,
  headerPostfix,
  subheaderId,
  actionComponent = null,
  styleName,
  height,
  tableSurface,
}) => {
  const classes = useStyles();
  return (
    <Paper
      elevation={0}
      className={`${classes.root} ${tableSurface ? classes.tableRoot : ''} ${styleName || ''}`.trim()}
    >
      {(headerId || headerPostfix || subheaderId || actionComponent) && (
        <Box className={`${classes.header} ${tableSurface ? classes.tableHeader : ''} d-flex align-items-start`}>
          <div className="mr-auto">
            {(headerId || headerPostfix) && (
              <Typography variant="h6" component="h3" style={{ margin: 0 }}>
                {headerId && <IntlMessages id={headerId} />}
                {headerPostfix || null}
              </Typography>
            )}
            {subheaderId && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                <IntlMessages id={subheaderId} />
              </Typography>
            )}
          </div>
          {actionComponent && <div className="ml-auto">{actionComponent}</div>}
        </Box>
      )}
      <div className={`${classes.body} ${tableSurface ? classes.tableBody : ''}`.trim()}>
        {
          isFetching || error
            ? <StubComponent error={error} height={height} isFetching={isFetching} />
            : children
        }
      </div>
    </Paper>
  );
};

const StubComponent = ({ error, height, isFetching }) => {
  if (isFetching) return <PlaceholderSpinner height={height} />;
  return <PlaceholderError error={error} height={height} />;
};

CardContainer.defaultProps = {
  children: null,
  actionComponent: null,
  headerId: null,
  subheaderId: null,
  styleName: null,
  isFetching: false,
  height: 500,
  headerPostfix: null,
  error: null,
  tableSurface: false,
};

CardContainer.propTypes = {
  children: PropTypes.node,
  actionComponent: PropTypes.node,
  headerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  subheaderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  styleName: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  isFetching: PropTypes.bool,
  height: PropTypes.number,
  headerPostfix: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  error: PropTypes.any,
  tableSurface: PropTypes.bool,
};

export default CardContainer;
